import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { task } from 'ember-concurrency'
import type { IntlService } from 'ember-intl'
import type { Log } from '@blakeelearning/log'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { not, eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import pick from 'teacher-ui-client/helpers/pick'
import InputText from 'teacher-ui-client/components/base/input-text'
import TextArea from 'teacher-ui-client/components/base/text-area'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import eventValue from 'teacher-ui-client/helpers/event-value'
import UiButtonLinkTo from 'teacher-ui-client/components/ui-button/link-to'
import UiButton from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import FidgetLoadingIndicator from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { CAPACITY_UNLIMITED } from 'teacher-ui-client/models/school-subscription'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'

type SubmitFormTaskResult = { ok: true } | { ok: false; response: Response }

interface Signature {
  Args: {
    softCapacity?: string // potentially "unlimited"
    partnerName: string
    partnerPhone: string
    partnerEmail: string
  }
}

interface FormParam {
  'agree[renewal]': 1
  number_of_students?: string
  three_year_info: 'yes' | 'no'
  instructions: string
}

export class SubscriptionFormReadingAu extends Component<Signature> {
  @service log!: Log

  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  @service authToken!: AuthToken

  @tracked comments = ''

  @tracked totalNumberOfStudents?: string

  @tracked agreeRenewal = false

  @tracked threeYearInfo = true

  get totalNumberOfStudentsOrCapacity(): string | undefined {
    return this.totalNumberOfStudents ?? this.args.softCapacity ?? '1000' // a default value if they have no school sub
  }

  @action
  setTotalNumberOfStudents(value: string) {
    this.totalNumberOfStudents = value
  }

  @action
  setAgreeRenewal(value: boolean) {
    this.agreeRenewal = value
  }

  @action
  setThreeYearInfo(value: boolean) {
    this.threeYearInfo = value
  }

  @action
  setComments(comments: string) {
    this.comments = comments
  }

  @action
  resetForm() {
    this.comments = ''
    this.totalNumberOfStudents = undefined
    this.agreeRenewal = false
    this.threeYearInfo = true
  }

  get subscriptionLabel(): string {
    return this.intl.t('subscriptions.reading')
  }

  get isFormDisabled() {
    return !this.agreeRenewal || this.isSubmitting
  }

  get isSubmitting() {
    return this.submitFormTask.isRunning
  }

  get formData(): FormParam {
    return {
      'agree[renewal]': 1,
      number_of_students: this.totalNumberOfStudentsOrCapacity,
      three_year_info: this.threeYearInfo ? 'yes' : 'no',
      instructions: this.comments,
    }
  }

  @action
  async submitForm(event: Event) {
    event.preventDefault()
    const result = await this.submitFormTask.perform(this.formData)
    if (result.ok) {
      const successMessage = this.intl.t('components.subscriptionForm.success')
      this.flashQueue.addSuccess({ subtitle: successMessage })
      this.resetForm()
    } else {
      const failureMessage = this.intl.t('components.subscriptionForm.error')
      this.flashQueue.addFail({ subtitle: failureMessage })
      this.log.error('re-au school subscription form submission failed', result.response)
    }
  }

  submitFormTask = task(async (params: FormParam): Promise<SubmitFormTaskResult> => {
    const formData = new FormData()
    for (const [name, value] of Object.entries(params)) {
      if (value) {
        formData.append(name, String(value))
      }
    }
    const url = join(config.readingEggsBaseUrl, '/re/school/subscription').href
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        accept: 'application/json',
        authorization: this.authToken.token ?? '',
      },
      body: formData,
    })
    if (response.ok) {
      return { ok: true }
    }
    return { ok: false, response }
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    <form {{on "submit" this.submitForm}}>
      <h4 class="my-3">{{t "components.subscriptionForm.title"}}</h4>
      <div class="border-dusty-black-100 border p-4">
        <strong class="block font-bold">{{t "components.subscriptionForm.renew.annualRenewal"}}</strong>
        <strong class="block font-bold">{{t "components.subscriptionForm.renew.renewFor12Months"}}</strong>
        <label class="my-6 block font-normal">
          <span class="mr-2">{{t "components.subscriptionForm.renew.totalNumberOfStudents"}}</span>
          {{#if (eq this.totalNumberOfStudentsOrCapacity CAPACITY_UNLIMITED)}}
            <strong class="font-bold">{{t "unlimited"}}</strong>
          {{else}}
            <InputText
              @themeKey={{themeKey "reading"}}
              class="w-32 font-bold"
              type="number"
              name="number_of_students"
              min="1"
              value={{this.totalNumberOfStudentsOrCapacity}}
              {{on "change" (pick "target.value" this.setTotalNumberOfStudents)}}
            />
          {{/if}}
        </label>
        <p class="mb-3">
          {{t "components.subscriptionForm.renew.threeYearInfo"}}
          <br />
          {{t "components.subscriptionForm.renew.wouldYouLikeThreeYearInfo"}}
        </p>
        <label class="my-1 flex items-center gap-4 font-normal">
          <input
            type="radio"
            name="three_year_info"
            checked={{this.threeYearInfo}}
            value="yes"
            {{on "change" (fn this.setThreeYearInfo true)}}
          />
          {{t "components.subscriptionForm.renew.threeYearInfoYes"}}
        </label>
        <label class="my-1 flex items-center gap-4 font-normal">
          <input
            type="radio"
            name="three_year_info"
            checked={{not this.threeYearInfo}}
            value="no"
            {{on "change" (fn this.setThreeYearInfo false)}}
          />
          {{t "components.subscriptionForm.renew.threeYearInfoNo"}}
        </label>
        <label class="my-6 block font-normal">
          <span class="font-bold">{{t "components.subscriptionForm.renew.comments"}}</span>
          <TextArea
            class="mt-2 w-full"
            rows="7"
            @value={{this.comments}}
            @themeKey={{themeKey "reading"}}
            {{on "input" (eventValue this.setComments)}}
          />
        </label>
        <label class="mb-3 flex items-center gap-6">
          <input
            {{on "change" (pick "target.checked" this.setAgreeRenewal)}}
            required="required"
            type="checkbox"
            checked={{this.agreeRenewal}}
            name="agree[renewal]"
          />
          <p>
            {{t
              "components.subscriptionForm.renew.agreeLegalese"
              partnerName=@partnerName
              subscription=this.subscriptionLabel
            }}
          </p>
        </label>
        <p class="my-4">
          {{t
            "components.subscriptionForm.contactCustomerService"
            phone=@partnerPhone
            email=@partnerEmail
            htmlSafe=true
          }}
        </p>
        <div>
          <UiButtonLinkTo @appearance={{ButtonAppearance.muted}} @route="subscription-type.home" class="w-24">
            {{t "cancel"}}
          </UiButtonLinkTo>
          <UiButton type="submit" @appearance={{ButtonAppearance.regular}} disabled={{this.isFormDisabled}}>
            {{t "components.subscriptionForm.renewOrUpgrade.submit"}}
          </UiButton>
          <FidgetLoadingIndicator @show={{this.isSubmitting}} @centered={{true}} @overlay={{true}} />
        </div>
        <div class="mt-3 text-sm">
          {{t "components.subscriptionForm.submitEffect" subscription=this.subscriptionLabel}}
        </div>
      </div>
    </form>
  </template>
}
